import { useEffect, useState } from "react"
import { ABILITIES, AUTOMATION, SPECIALIZATIONS, SPEC_UPGRADES, UPGRADES, costFor, type UpgradeDef } from "@cookies/shared/catalog/display"
import { BuyToast, Panel, PurchaseLedger } from "../components/ui"
import { useBakeryStore } from "../game/bakeryStore"
import { fmtInt, specLabel, toNum } from "../game/format"
import { bakerLevelForXp as sharedBakerLevelForXp, bakerThresholdForLevel as sharedThreshold } from "@cookies/shared/levels"
import { SPEC_UNLOCK_LEVEL as SPEC_UNLOCK } from "@cookies/shared/config"

type UTab = "click" | "auto" | "specline" | "spec" | "abilities"

const TABS: { id: UTab; label: string; icon: string }[] = [
  { id: "click", label: "Click", icon: "✋" },
  { id: "auto", label: "Automation", icon: "🤖" },
  { id: "specline", label: "Spec Line", icon: "🌟" },
  { id: "spec", label: "Specialization", icon: "🛠" },
  { id: "abilities", label: "Abilities", icon: "✨" },
]

const UPGRADE_NAMES = new Map<string, string>(
  [...UPGRADES, ...AUTOMATION, ...SPEC_UPGRADES].map((d) => [d.id, d.name] as [string, string]),
)

function reqDisplayName(id: string): string {
  const named = UPGRADE_NAMES.get(id)
  if (named !== undefined && named !== "") return named
  return id.replace(/_/g, " ")
}

function levelOfUpgrade(
  live: Record<string, number> | undefined,
  rows: { id: string; level: number }[] | undefined,
  id: string,
): number {
  const want = id.trim().toLowerCase()
  if (want === "") return 0
  if (live !== undefined) {
    for (const [k, v] of Object.entries(live)) {
      if (k.trim().toLowerCase() === want && typeof v === "number" && Number.isFinite(v)) return Math.floor(v)
    }
  }
  if (rows) {
    const row = rows.find((u) => u.id.trim().toLowerCase() === want)
    if (row && Number.isFinite(row.level)) return Math.floor(row.level)
  }
  return 0
}

function thresholdForLevel(level: number): number {
  return Number(sharedThreshold(level))
}

function bakerLevelForXp(xp: number): number {
  if (!Number.isFinite(xp) || xp < 0) return 1
  return sharedBakerLevelForXp(BigInt(Math.floor(xp)))
}

function bakerProgress(xp: number, level: number): { cur: number; next: number; pct: number } {
  const cur = thresholdForLevel(level)
  const next = thresholdForLevel(level + 1)
  const span = next - cur
  const pct = span <= 0 ? 100 : Math.max(0, Math.min(100, ((xp - cur) / span) * 100))
  return { cur, next, pct }
}

function bakerInfoForSnap(snap: ReturnType<typeof useBakeryStore>["snapshot"]): { level: number; xp: number; next: number; pct: number } {
  const xp = snap ? toNum(snap.me.xp ?? snap.me.totalClicks ?? "0") : 0
  const lvl = snap?.me.level !== undefined ? snap.me.level : snap ? 1 : bakerLevelForXp(xp)
  if (snap?.me.nextLevelXp !== undefined) {
    const next = toNum(snap.me.nextLevelXp)
    const cur = thresholdForLevel(lvl)
    const span = Math.max(1, next - cur)
    const pct = Math.max(0, Math.min(100, ((xp - cur) / span) * 100))
    return { level: lvl, xp, next, pct }
  }
  const { next, pct } = bakerProgress(xp, lvl)
  return { level: lvl, xp, next, pct }
}

function UpgradeCard({ def, onToast }: { def: UpgradeDef; onToast: (m: string) => void }) {
  const store = useBakeryStore()
  const snap = store.snapshot
  const connected = store.connected
  const liveCredits = store.liveCredits
  const snapBaker = bakerInfoForSnap(snap)
  void snapBaker
  const specChosen = snap?.me.specialization ?? ""
  const isPending = store.isPending("upgrade", def.id)
  const onBuy = (): void => store.buyUpgrade(def.id)
  const serverRow = snap?.upgrades?.find((u) => u.id === def.id)
  const maxLevel = serverRow?.maxLevel ?? def.maxLevel
  const level = serverRow?.level ?? snap?.me.upgrades?.[def.id] ?? 0
  const serverCost = serverRow?.cost
  const cost = serverCost !== undefined ? toNum(serverCost) : costFor(def, level)
  const credits = liveCredits
  const maxed = level >= maxLevel
  const specNeed = (serverRow?.specLocked || def.specLocked || "").trim()
  const reqId = (serverRow?.requiresId || def.requiresId || "").trim()
  const reqLv = serverRow?.requiresLevel ?? def.requiresLevel ?? 0
  const reqHave = reqId === "" ? reqLv : levelOfUpgrade(snap?.me.upgrades, snap?.upgrades, reqId)
  const reqName = reqId === "" ? "" : reqDisplayName(reqId)
  const specLocked = specNeed !== "" && specChosen.trim().toLowerCase() !== specNeed.toLowerCase()
  const tierLocked = reqId !== "" && reqLv > 0 && reqHave < reqLv
  const serverLocked = serverRow?.locked === true
  const bakeryLv = snap?.bakery.level ?? 1
  const unlockLv = serverRow?.unlockBakeryLevel ?? def.unlockLevel
  const bakerLocked = bakeryLv < unlockLv
  const disabled = serverRow?.disabled === true || (level > 0 && specLocked)
  const locked = !maxed && (bakerLocked || specLocked || tierLocked || serverLocked)
  const lockText = disabled ? `Disabled — Lv ${level} kept, switch back to ${specNeed} to reactivate (bonuses off)` : bakerLocked ? `Requires Bakery Lv ${unlockLv} (Bakery Lv ${bakeryLv})` : specLocked ? `Requires ${specNeed} spec (you must be ${specNeed})` : tierLocked ? `Requires ${reqName} Lv ${reqLv} (${reqHave}/${reqLv})` : serverLocked ? "Locked" : ""
  const affordable = credits >= cost
  const pending = isPending
  const owned = level > 0
  const canBuy = connected && snap !== null && !maxed && !locked && affordable && !pending
  const pendingReason = !connected ? "Not connected — retrying…" : "Buying…"
  const cardClass = maxed ? "cb-card maxed" : locked ? "cb-card muted" : pending ? "cb-card confirming" : affordable ? "cb-card" : "cb-card muted"
  return (
    <div className={cardClass} aria-busy={pending || undefined}>
      <span className={pending ? "cb-card-ico cookie-pulse" : "cb-card-ico"} aria-hidden="true">{def.icon}</span>
      <div className="cb-card-body">
        <b>{def.name}</b>
        <small>{def.desc}</small>
        <small>
          <span className={pending ? "cb-lvl confirming-pulse" : "cb-lvl"}>{owned ? `Owned Lv ${level}/${maxLevel}` : `Lv ${level}/${maxLevel}`}</span> <span className="dim">{def.effect}</span>
          {pending && <span className="cb-pill confirming" role="status">Buying…</span>}
        </small>
        {specNeed !== "" && <small className="dim">Spec: {specNeed} (you must be {specNeed}){disabled ? " · Disabled (bonuses off, levels kept)" : ""}</small>}
        {reqId !== "" && reqLv > 0 && (tierLocked
          ? <small className="dim">Needs {reqName} Lv {reqLv} ({reqHave}/{reqLv})</small>
          : <small className="dim">✓ {reqName} Lv {reqLv} ({reqHave}/{reqLv})</small>)}
        {locked && (
          <div style={{ marginTop: 6 }}>
            <small className="dim">{lockText}</small>
          </div>
        )}
      </div>
      <span style={{ textAlign: "right", minWidth: 92 }}>
        {maxed ? (
          <span className="cb-max">Maxed</span>
        ) : (
          <>
            <div className={affordable ? "cb-cost" : "cb-cost poor"}>🪙 {fmtInt(cost)}</div>
            {disabled ? (
              <button type="button" className="cb-buy off" onClick={() => onToast(lockText)}>Disabled</button>
            ) : locked ? (
              <button type="button" className="cb-buy off" onClick={() => onToast(lockText)}>Locked</button>
            ) : pending ? (
              <button type="button" disabled aria-busy="true" title={pendingReason} className="cb-buy confirming">
                Buying…
              </button>
            ) : (
              <button
                type="button"
                disabled={!canBuy}
                title={!connected ? "Not connected — retrying…" : !affordable ? `Need 🪙 ${fmtInt(cost)}` : "Buy next level"}
                className={canBuy ? "cb-buy" : "cb-buy off"}
                onClick={onBuy}
              >
                {affordable ? "Buy" : fmtInt(cost)}
              </button>
            )}
          </>
        )}
      </span>
    </div>
  )
}

function fmtSecs(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rest = s % 60
  return rest === 0 ? `${m}m` : `${m}m ${rest}s`
}

function AbilitiesList({ affordableOnly, onToast }: { affordableOnly: boolean; onToast: (m: string) => void }) {
  const store = useBakeryStore()
  const snap = store.snapshot as NonNullable<ReturnType<typeof useBakeryStore>["snapshot"]>
  const connected = store.connected
  const liveCredits = store.liveCredits
  const snapBaker = bakerInfoForSnap(snap)
  const baker = snapBaker
  const specChosen = snap?.me.specialization ?? ""
  const isPending = (id: string): boolean => store.isPending("ability", id)
  const onActivate = (id: string): void => store.activateAbility(id)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])
  const serverRows = new Map((snap.abilities ?? []).map((a) => [a.id, a]))
  const pool = ABILITIES.filter((a) => {
    if (!affordableOnly) return true
    const row = serverRows.get(a.id)
    if (row !== undefined) return !row.locked && row.cooldownRemainingMs <= 0
    return baker.level >= a.unlockLevel
  })
  if (pool.length === 0 && affordableOnly) {
    return <div className="cb-panel center small" role="status">Nothing usable right now — abilities are locked, recharging, or cost more credits.</div>
  }
  return (
    <>
      {pool.map((a) => {
        const row = serverRows.get(a.id)
        const cost = row !== undefined ? toNum(row.cost) : a.cost
        const cooldownMs = row?.cooldownMs ?? a.cooldownSec * 1000
        const cooldownLeft = row !== undefined ? Math.max(0, row.cooldownRemainingMs - Math.max(0, now - snap.serverTime)) : 0
        const activeLeft = row !== undefined ? Math.max(0, row.activeRemainingMs - Math.max(0, now - snap.serverTime)) : 0
        const specNeed = (row?.specLocked || a.specLocked || "").trim()
        const lockReason = row?.lockReason ?? ""
        const specLocked = specNeed !== "" && specChosen.trim().toLowerCase() !== specNeed.toLowerCase()
        const levelLocked = row !== undefined ? (row.locked && (lockReason === "locked" || (lockReason === "" && baker.level < (row.unlockBakerLevel ?? a.unlockLevel)))) : baker.level < a.unlockLevel
        const locked = row !== undefined ? row.locked : (specLocked || levelLocked)
        const needLevel = row?.unlockBakerLevel ?? a.unlockLevel
        const need = thresholdForLevel(needLevel)
        const prog = (() => {
          const cur = thresholdForLevel(needLevel - 1)
          const span = need - cur
          return span <= 0 ? 100 : Math.max(0, Math.min(100, ((baker.xp - cur) / span) * 100))
        })()
        const lockText = specLocked ? `Requires ${specNeed} spec` : levelLocked ? `Requires Baker Lv ${needLevel} (${Math.floor(baker.xp)}/${need} XP)` : row?.locked ? (lockReason === "locked_spec" ? `Requires ${specNeed} spec` : "Locked") : ""
        const affordable = liveCredits >= cost
        const pending = isPending(a.id)
        const recharging = cooldownLeft > 0
        const active = activeLeft > 0
        const canUse = connected && !locked && !recharging && affordable && !pending
        const cardClass = locked || (!affordable && !pending) ? "cb-card muted" : pending ? "cb-card confirming" : active ? "cb-card gold-glow" : "cb-card"
        const cdPct = cooldownMs > 0 && recharging ? Math.max(0, Math.min(100, (cooldownLeft / cooldownMs) * 100)) : 0
        return (
          <div key={a.id} className={cardClass} aria-busy={pending || undefined}>
            <span className={pending ? "cb-card-ico cookie-pulse" : "cb-card-ico"} aria-hidden="true">{a.icon}</span>
            <div className="cb-card-body">
              <b>{a.name}{active && " ⚡"}</b>
              <small>{row?.description || a.desc}</small>
              <small>
                <span className="dim">{a.effect}</span>
                {pending && <span className="cb-pill confirming" role="status">Using…</span>}
                {active && <span className="cb-pill" role="status">Active {fmtSecs(activeLeft)} left</span>}
              </small>
              {specNeed !== "" && <small className="dim">Spec: {specNeed}</small>}
              <small className="dim">Cooldown {a.cooldownSec >= 60 ? `${Math.floor(a.cooldownSec / 60)}m` : `${a.cooldownSec}s`}{recharging ? ` · Recharging ${fmtSecs(cooldownLeft)}` : " · Ready"}</small>
              {recharging && <div className="cb-track" style={{ height: 8, marginTop: 4 }}><div className="cb-fill" style={{ width: `${cdPct}%` }} /></div>}
              {locked && (
                <div style={{ marginTop: 6 }}>
                  <small className="dim">{lockText}</small>
                  {levelLocked && <div className="cb-track" style={{ height: 8, marginTop: 4 }}><div className="cb-fill" style={{ width: `${prog}%` }} /></div>}
                </div>
              )}
            </div>
            <span style={{ textAlign: "right", minWidth: 92 }}>
              <div className={affordable ? "cb-cost" : "cb-cost poor"}>🪙 {fmtInt(cost)}</div>
              {locked ? (
                <button type="button" className="cb-buy off" onClick={() => onToast(lockText || "Locked")}>Locked</button>
              ) : pending ? (
                <button type="button" disabled aria-busy="true" title="Activating…" className="cb-buy confirming">
                  Using…
                </button>
              ) : recharging ? (
                <button type="button" className="cb-buy off" onClick={() => onToast(`Recharging (${fmtSecs(cooldownLeft)} left)`)}>{fmtSecs(cooldownLeft)}</button>
              ) : (
                <button
                  type="button"
                  disabled={!canUse}
                  title={!connected ? "Not connected — retrying…" : !affordable ? `Need 🪙 ${fmtInt(cost)}` : `Use ${a.name}`}
                  className={canUse ? "cb-buy" : "cb-buy off"}
                  onClick={() => onActivate(a.id)}
                >
                  {affordable ? "Use" : fmtInt(cost)}
                </button>
              )}
            </span>
          </div>
        )
      })}
    </>
  )
}

const AFFORDABLE_ONLY_KEY = "cb.upgrades.showAffordableOnly"
function readAffordableOnly(): boolean {
  try {
    return localStorage.getItem(AFFORDABLE_ONLY_KEY) === "1"
  } catch {
    return false
  }
}

export default function UpgradesPage() {
  const [tab, setTab] = useState<UTab>("click")
  const [localToast, setLocalToast] = useState<string | null>(null)
  const [pendingSpec, setPendingSpec] = useState<string | null>(null)
  const [showAffordableOnly, setShowAffordableOnly] = useState<boolean>(readAffordableOnly)
  const store = useBakeryStore()
  const snap = store.snapshot
  const connected = store.connected
  const baker = bakerInfoForSnap(snap)
  const liveCredits = store.liveCredits
  const specChosen = snap?.me.specialization ?? ""
  const pool = tab === "click" ? UPGRADES : tab === "auto" ? AUTOMATION : SPEC_UPGRADES
  const specChosenLabel = specChosen ? specLabel(specChosen) : "Not chosen"
  const lastSpecLevelRaw = (snap?.me as unknown as Record<string, unknown> | undefined)?.["lastSpecLevel"]
  const lastSpecLevel = typeof lastSpecLevelRaw === "number" && Number.isFinite(lastSpecLevelRaw) ? Math.floor(lastSpecLevelRaw) : 0
  const specCooldown = lastSpecLevel > 0 && baker.level <= lastSpecLevel && specChosen !== ""
  const toggleAffordableOnly = () => {
    setShowAffordableOnly((v) => {
      const next = !v
      try {
        localStorage.setItem(AFFORDABLE_ONLY_KEY, next ? "1" : "0")
      } catch {
        return next
      }
      return next
    })
  }
  const isUpgradeBuyable = (def: UpgradeDef): boolean => {
    const serverRow = snap?.upgrades?.find((u) => u.id === def.id)
    const maxLevel = serverRow?.maxLevel ?? def.maxLevel
    const level = serverRow?.level ?? snap?.me.upgrades?.[def.id] ?? 0
    if (level >= maxLevel) return false
    const cost = serverRow?.cost !== undefined ? toNum(serverRow.cost) : costFor(def, level)
    const specNeed = (serverRow?.specLocked || def.specLocked || "").trim()
    const reqId = (serverRow?.requiresId || def.requiresId || "").trim()
    const reqLv = serverRow?.requiresLevel ?? def.requiresLevel ?? 0
    const reqHave = reqId === "" ? reqLv : levelOfUpgrade(snap?.me.upgrades, snap?.upgrades, reqId)
    const specLocked = specNeed !== "" && specChosen.trim().toLowerCase() !== specNeed.toLowerCase()
    const tierLocked = reqId !== "" && reqLv > 0 && reqHave < reqLv
    const serverLocked = serverRow?.locked === true
    const disabled = serverRow?.disabled === true || (level > 0 && specLocked)
    if (disabled) return false
    const bakeryLevel = snap?.bakery.level ?? 1
    const unlockLevel = serverRow?.unlockBakeryLevel ?? def.unlockLevel
    if (bakeryLevel < unlockLevel || specLocked || tierLocked || serverLocked) return false
    return liveCredits >= cost
  }
  const poolFiltered = showAffordableOnly ? pool.filter(isUpgradeBuyable) : pool
  const push = (m: string) => {
    setLocalToast(m)
    window.setTimeout(() => setLocalToast((t) => (t === m ? null : t)), 2200)
  }
  const toastNotice = store.buyNotice !== null && (store.buyNotice.kind === "upgrade" || store.buyNotice.kind === "buy" || store.buyNotice.kind === "spec") ? store.buyNotice : null
  const toast = toastNotice ?? (localToast ? { id: -1, text: localToast, kind: "local", ok: false } : null)

  return (
    <div className="cb-page">
      <Panel className="cb-hero-row cb-hero-sticky">
        <div style={{ flex: 1 }}>
          <small className="dim">Baker Credits</small>
          <div className="cb-big">🪙 {fmtInt(liveCredits)}</div>
          <div style={{ marginTop: 8 }}>
            <small className="dim">Baker Lv {baker.level} — {baker.xp}/{baker.next} XP to Lv {baker.level + 1}</small>
            <div className="cb-track" style={{ height: 8 }}><div className="cb-fill" style={{ width: `${baker.pct}%` }} /></div>
            <small className="dim">Lv {baker.level} → Lv {baker.level + 1} needs {baker.next - baker.xp} XP (clicks)</small>
          </div>
        </div>
        <div className="cb-hero-right">
          <span className="cb-spec-ava">👨‍🍳</span>
          <div>
            <small className="dim">Specialization</small>
            <div><b>{specChosenLabel}</b> · Baker Lv {baker.level}</div>
            <small className="dim">Bakery Lv {snap?.bakery.level ?? 1}</small>
          </div>
        </div>
      </Panel>

      {snap === null && (
        <div className="cb-panel center small" role="status" aria-live="polite">
          Loading upgrades…
        </div>
      )}
      {toast && <BuyToast notice={toast} />}
      <PurchaseLedger history={store.purchaseHistory} kinds={["upgrade"]} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div className="cb-tabs">
          {TABS.map((t) => (
            <button type="button" key={t.id} onClick={() => setTab(t.id)} className={tab === t.id ? "cb-tab active" : "cb-tab"}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={toggleAffordableOnly}
          aria-pressed={showAffordableOnly}
          title={showAffordableOnly ? "Show all upgrades" : "Show only affordable + unlocked upgrades"}
          className={showAffordableOnly ? "cb-tab active" : "cb-tab"}
        >
          {showAffordableOnly ? "🪙 Affordable ✓" : "🪙 Affordable"}
        </button>
      </div>

      {tab === "auto" && <div className="cb-panel small" style={{ background: "#fff9e6", border: "2px dashed #3a2115" }}><small className="dim">Live only — stirs the dough automatically while present. Base modules still tick quietly when empty.</small></div>}
      <div className="cb-stack">
        {(tab === "click" || tab === "auto" || tab === "specline") && (
          snap === null ? (
            <div className="cb-panel center small" role="status">Loading…</div>
          ) : poolFiltered.length === 0 && showAffordableOnly ? (
            <div className="cb-panel center small" role="status">Nothing affordable + unlocked right now — earn more credits or level up.</div>
          ) : (
            poolFiltered.map((d) => <UpgradeCard key={d.id} def={d} onToast={push} />)
          )
        )}

        {tab === "spec" && (
          snap === null ? (
            <div className="cb-panel center small" role="status">Loading…</div>
          ) : (
            (showAffordableOnly ? SPECIALIZATIONS.filter(() => baker.level >= SPEC_UNLOCK && !specCooldown) : SPECIALIZATIONS).map((s) => {
              const locked = baker.level < SPEC_UNLOCK
              const isChosen = specChosen.toLowerCase() === s.id.toLowerCase()
              const onCooldown = !locked && specCooldown && !isChosen
              const prog = (() => {
                const cur = thresholdForLevel(SPEC_UNLOCK - 1)
                const nxt = thresholdForLevel(SPEC_UNLOCK)
                const span = nxt - cur
                return span <= 0 ? 100 : Math.max(0, Math.min(100, ((baker.xp - cur) / span) * 100))
              })()
              const cdNeed = thresholdForLevel(lastSpecLevel + 1)
              const cdCur = thresholdForLevel(Math.max(1, lastSpecLevel))
              const cdSpan = cdNeed - cdCur
              const cdPct = cdSpan <= 0 ? 100 : Math.max(0, Math.min(100, ((baker.xp - cdCur) / cdSpan) * 100))
              const cdRemaining = Math.max(0, cdNeed - Math.floor(baker.xp))
              const pending = store.isPending("spec", s.id)
              const lockTitle = `Requires Baker Lv ${SPEC_UNLOCK} (${Math.floor(baker.xp)}/${thresholdForLevel(SPEC_UNLOCK)} XP)`
              const cdTitle = `Cooldown — change unlocks at Lv ${lastSpecLevel + 1} (${Math.floor(baker.xp)}/${cdNeed} XP, ${cdRemaining} XP left)`
              return (
                <div key={s.id} className={isChosen ? "cb-card gold-glow" : locked || onCooldown ? "cb-card muted" : "cb-card"}>
                  <span className="cb-card-ico">{s.icon}</span>
                  <div className="cb-card-body">
                    <b>{s.name} {isChosen && "✓"}</b>
                    <small>{s.desc}</small>
                    <small className="dim">Tool: {s.tool}</small>
                    <small className="dim">{s.perks.join(" · ")}</small>
                    {locked && (
                      <div style={{ marginTop: 6 }}>
                        <small className="dim">Requires Lv{SPEC_UNLOCK} ({Math.floor(baker.xp)}/{thresholdForLevel(SPEC_UNLOCK)} XP)</small>
                        <div className="cb-track" style={{ height: 8, marginTop: 4 }}><div className="cb-fill" style={{ width: `${prog}%` }} /></div>
                      </div>
                    )}
                    {onCooldown && (
                      <div style={{ marginTop: 6 }}>
                        <small className="dim">Cooldown: Lv {baker.level} → Lv {lastSpecLevel + 1} needs {cdRemaining} XP ({Math.floor(baker.xp)}/{cdNeed} XP)</small>
                        <div className="cb-track" style={{ height: 8, marginTop: 4 }}><div className="cb-fill" style={{ width: `${cdPct}%` }} /></div>
                      </div>
                    )}
                  </div>
                  {isChosen ? (
                    <span className="cb-max">Chosen</span>
                  ) : locked ? (
                    <button type="button" disabled title={lockTitle} aria-disabled="true" className="cb-buy off">Locked</button>
                  ) : onCooldown ? (
                    <button type="button" disabled title={cdTitle} aria-disabled="true" className="cb-buy off">Cooldown</button>
                  ) : pending ? (
                    <button type="button" disabled aria-busy="true" title="Choosing…" className="cb-buy confirming">
                      Choosing…
                    </button>
                  ) : (
                    <button type="button" disabled={!connected} title={!connected ? "Not connected — retrying…" : `Choose ${s.name}`} className={connected ? "cb-buy ghost" : "cb-buy off"} onClick={() => setPendingSpec(s.id)}>Choose</button>
                  )}
                </div>
              )
            })
          )
        )}

        {tab === "abilities" && (
          snap === null ? (
            <div className="cb-panel center small" role="status">Loading…</div>
          ) : (
            <AbilitiesList affordableOnly={showAffordableOnly} onToast={push} />
          )
        )}
      </div>
      {pendingSpec !== null && (
        <div className="cb-modal-bg" role="dialog" aria-modal="true">
          <div className="cb-modal">
            <h3>Change specialisation?</h3>
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>You can change again next level (cooldown)</div>
            <div className="cb-modal-row">
              <button type="button" className="cb-buy off" onClick={() => setPendingSpec(null)}>Cancel</button>
              <button type="button" className="cb-buy" onClick={() => { const id = pendingSpec; setPendingSpec(null); if (id) store.chooseSpec(id) }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
